import api, { signOutOnXimiSessionLost } from '../../utilities/api';
import { fetchXimiBeneficiaries } from './ximiBeneficiaries';
import { isXimiSessionLostCode } from './ximiStimulinSession';

const SYNC_PATH = '/caregiver-assignments/sync';

let inFlight: Promise<boolean> | null = null;

/**
 * Relaie le périmètre Xelya vers StimulinAPI : Ozmo signe la liste, StimulinAPI la vérifie et
 * l'applique — dans le sens du rôle de l'appelant (un aidant/agent pousse ses bénéficiaires,
 * un bénéficiaire pousse ses aidants). Tout utilisateur Ximi peut donc l'appeler.
 *
 * Ne lève jamais — l'appelant décide quoi faire d'un échec, selon ce qu'il affiche :
 *   · liste de bénéficiaires d'un intervenant → BLOQUANT, message d'erreur si `false`
 *     (elle mène à l'accès aux données d'un bénéficiaire : jamais de relation périmée) ;
 *   · galaxie familiale, après login → best effort, résultat ignoré.
 *
 * Dédoublonnage en vol : login et ouverture d'écran peuvent tirer à une seconde d'écart, et
 * chaque synchro consomme un jeton Ozmo à usage unique (`jti`). Sans ce garde, deux jetons
 * seraient brûlés pour le même résultat. Pas de TTL, volontairement : il n'économiserait que
 * des appels invisibles au prix d'une péremption silencieuse après une modification au
 * back-office.
 */
export async function ensureBeneficiariesSynced(): Promise<boolean> {
    if (inFlight) return inFlight;
    inFlight = runSync().finally(() => {
        inFlight = null;
    });
    return inFlight;
}

/** `true` si le périmètre a été appliqué — le seul cas où une liste bloquante peut s'afficher. */
async function runSync(): Promise<boolean> {
    const list = await fetchXimiBeneficiaries();
    if (!list.ok) {
        // Un autre appareil a pris la session (ou l'option est coupée) : on se déconnecte ici,
        // sans attendre le prochain refresh.
        if (isXimiSessionLostCode(list.code)) {
            await signOutOnXimiSessionLost(list.code);
            return false;
        }
        // eslint-disable-next-line no-console
        console.warn('[XimiSync] liste Ozmo indisponible', list.code ?? list.reason);
        return false;
    }
    try {
        await api.post(SYNC_PATH, { token: list.token });
        return true;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[XimiSync] échec de la synchro', error);
        return false;
    }
}
